import { convertToLbs } from "@/routes/dashboard/components/patient-summary-pages/weight-trend/weight-config";
import {
  ChfStateCode,
  DEVICE_READING_CONTEXT_SYSTEM,
  DEVICE_READING_CONTEXT_URL,
  HL7_OBS_INTERPRETATION_SYSTEM,
  LOINC_SYSTEM,
  ReadingContextCode,
  VITALL_CHF_STATE_SYSTEM,
  VITALL_CHF_WITH_MED_SYSTEM,
  VITALL_OBS_INTERPRETATION_SYSTEM,
  VITALL_SEVERITY_SYSTEM,
  VitalInterpretationCode,
  WithMedCode,
} from "@/utils/fhir-constants";

import { fhirR4 } from "@smile-cdr/fhirts";

/**
 * Extracts the reading context display string from an observation's extension.
 */
const VALID_READING_CONTEXT_CODES = new Set<string>(Object.values(ReadingContextCode));

export function extractReadingContext(obs: fhirR4.Observation): ReadingContextCode | undefined {
  const ext = obs.extension?.find(e => e.url === DEVICE_READING_CONTEXT_URL);
  if (ext?.valueCoding?.system && ext.valueCoding.system !== DEVICE_READING_CONTEXT_SYSTEM) {
    return undefined;
  }
  const code = ext?.valueCoding?.code;
  if (code && VALID_READING_CONTEXT_CODES.has(code)) {
    return code as ReadingContextCode;
  }
  return undefined;
}

/**
 * FHIR code systems and constants for observation categories.
 */
export const OBSERVATION_INTERPRETATION_SYSTEM = "http://hl7.org/fhir/v2/0078";
export const OBSERVATION_CATEGORY_SYSTEM = "http://hl7.org/fhir/observation-category";
export const QUESTIONNAIRE_DOMAIN_REFERENCE_ITEM = "QUESTIONNAIRE_DOMAIN_REFERENCE_ITEM";
export const QUESTIONNAIRE_RESPONSE_ITEM = "QUESTIONNAIRE_RESPONSE_ITEM";
export const MESSAGE_TO_PATIENT_CODE = "MESSAGE_TO_PATIENT";

/** LOINC codes for vital sign components */
const LOINC_SYSTOLIC = "8480-6";
const LOINC_DIASTOLIC = "8462-4";
const LOINC_HEART_RATE = "8867-4";
const LOINC_WEIGHT = "29463-7";
const LOINC_WEIGHT_MEASURED = "3141-9";

export interface VitalValue {
  value: number;
  unit: string;
  interpretation?: string;
}

export interface ExtractedVitals {
  weight?: VitalValue;
  heartRate?: VitalValue;
  systolic?: VitalValue;
  diastolic?: VitalValue;
}

/**
 * Returns the interpretation code from an observation or component's interpretation array.
 * Searches Vitall system first, then HL7 system. Only returns recognized VitalInterpretationCode values.
 */
const VALID_INTERPRETATION_CODES = new Set<string>(Object.values(VitalInterpretationCode));

export const getInterpretationCode = (interpretations: fhirR4.CodeableConcept[] | undefined): string | undefined => {
  if (!interpretations || interpretations.length === 0) {
    return undefined;
  }

  // TODO: HL7 fallback can be removed after the Vitall interpretation system is settled
  const systems = [VITALL_OBS_INTERPRETATION_SYSTEM, HL7_OBS_INTERPRETATION_SYSTEM];

  for (const system of systems) {
    for (const entry of interpretations) {
      const coding = entry.coding?.find(c => c.system === system);
      if (coding?.code && VALID_INTERPRETATION_CODES.has(coding.code)) {
        return coding.code;
      }
    }
  }

  return undefined;
};

/**
 * Extracts a quantity value from an observation component matching a LOINC code.
 */
const extractComponentByLoincCode = (
  components: fhirR4.ObservationComponent[] | undefined,
  loincCode: string
): VitalValue | undefined => {
  if (!components) {
    return undefined;
  }
  const comp = components.find(c => c.code?.coding?.some(coding => coding.code === loincCode));
  if (!comp?.valueQuantity?.value) {
    return undefined;
  }
  return {
    value: comp.valueQuantity.value,
    unit: comp.valueQuantity.unit || "",
    interpretation: getInterpretationCode(comp.interpretation),
  };
};

/**
 * Extracts vital signs (weight, heart rate, blood pressure) from a composite Observation.
 * Handles both component-based (BP panel) and top-level value observations.
 */
export const extractVitalsFromObservation = (observation: fhirR4.Observation): ExtractedVitals => {
  const vitals: ExtractedVitals = {};
  const hasLoincCode = (loincCode: string): boolean =>
    observation.code?.coding?.some(c => c.system === LOINC_SYSTEM && c.code === loincCode) ?? false;
  const isWeightObs = hasLoincCode(LOINC_WEIGHT) || hasLoincCode(LOINC_WEIGHT_MEASURED);
  const obsLoincCode = observation.code?.coding?.find(c => c.system === LOINC_SYSTEM)?.code;
  const topInterpretation = getInterpretationCode(observation.interpretation);

  // Top-level weight observation (convert to lbs)
  if (isWeightObs && observation.valueQuantity?.value) {
    const lbsValue = convertToLbs(observation.valueQuantity.value, observation.valueQuantity.code);
    vitals.weight = {
      value: parseFloat(lbsValue.toFixed(1)),
      unit: "lbs",
      interpretation: topInterpretation,
    };
  }

  // Top-level heart rate observation
  if (obsLoincCode === LOINC_HEART_RATE && observation.valueQuantity?.value) {
    vitals.heartRate = {
      value: observation.valueQuantity.value,
      unit: observation.valueQuantity.unit || "bpm",
      interpretation: topInterpretation,
    };
  }

  // BP panel components
  const systolic = extractComponentByLoincCode(observation.component, LOINC_SYSTOLIC);
  const diastolic = extractComponentByLoincCode(observation.component, LOINC_DIASTOLIC);
  if (systolic) {
    vitals.systolic = { ...systolic, interpretation: systolic.interpretation || topInterpretation };
  }
  if (diastolic) {
    vitals.diastolic = { ...diastolic, interpretation: diastolic.interpretation || topInterpretation };
  }

  // Also check components for weight and heart rate (composite observations)
  const weightComp =
    extractComponentByLoincCode(observation.component, LOINC_WEIGHT) ??
    extractComponentByLoincCode(observation.component, LOINC_WEIGHT_MEASURED);
  if (weightComp && !vitals.weight) {
    const comp = observation.component?.find(c =>
      c.code?.coding?.some(coding => coding.code === LOINC_WEIGHT || coding.code === LOINC_WEIGHT_MEASURED)
    );
    const lbsValue = convertToLbs(weightComp.value, comp?.valueQuantity?.code);
    vitals.weight = { ...weightComp, value: parseFloat(lbsValue.toFixed(1)), unit: "lbs" };
  }

  const hrComp = extractComponentByLoincCode(observation.component, LOINC_HEART_RATE);
  if (hrComp && !vitals.heartRate) {
    vitals.heartRate = hrComp;
  }

  return vitals;
};

/**
 * Extracts the "Message To Patient" text from an Observation's components.
 */
export const extractMessageToPatient = (observation: fhirR4.Observation): string | undefined => {
  const messageComponent = observation.component?.find(c =>
    c.code?.coding?.some(coding => coding.code === MESSAGE_TO_PATIENT_CODE)
  );
  return messageComponent?.valueString;
};

/**
 * Extracts all message-to-patient texts from an algorithm result Observation's note array.
 */
export const extractMessagesFromAlgorithmResult = (observation: fhirR4.Observation): string[] => {
  if (!observation.note || observation.note.length === 0) {
    return [];
  }
  return observation.note.map(n => n.text).filter((text): text is string => !!text);
};

/**
 * Gets the related QuestionnaireResponse reference from an Observation.
 */
export const getRelatedQuestionnaireResponseRef = (observation: fhirR4.Observation): string | undefined => {
  const related = (observation as unknown as Record<string, unknown>).related as
    { target?: { reference?: string } }[] | undefined;
  if (!related || related.length === 0) {
    return undefined;
  }
  return related[0]?.target?.reference;
};

/**
 * Utility function to extract a code by system from a CodeableConcept.
 * @param concept - The CodeableConcept.
 * @param system - The system string.
 * @returns The code if found; otherwise, undefined.
 */
export function getCodeBySystem(concept: fhirR4.CodeableConcept, system: string): string | undefined {
  return concept.coding?.find(coding => coding.system === system)?.code;
}

export interface AlgorithmInterpretation {
  severity?: VitalInterpretationCode;
  chfState?: ChfStateCode;
  withMed?: WithMedCode;
}

const SEVERITY_CODE_VALUES = new Set<string>(Object.values(VitalInterpretationCode));
const CHF_STATE_CODE_VALUES = new Set<string>(Object.values(ChfStateCode));
const WITH_MED_CODE_VALUES = new Set<string>(Object.values(WithMedCode));

/**
 * Extracts algorithm interpretation from the new 3-entry Vitall format.
 * Falls back to old HL7 single-entry format for severity if no Vitall severity is found.
 */
export const extractAlgorithmInterpretation = (
  interpretation: fhirR4.CodeableConcept[] | undefined
): AlgorithmInterpretation => {
  if (!interpretation || interpretation.length === 0) {
    return {};
  }

  const result: AlgorithmInterpretation = {};

  for (const entry of interpretation) {
    const severityCode = getCodeBySystem(entry, VITALL_SEVERITY_SYSTEM);
    if (severityCode && SEVERITY_CODE_VALUES.has(severityCode)) {
      result.severity = severityCode as VitalInterpretationCode;
    }

    const chfState = getCodeBySystem(entry, VITALL_CHF_STATE_SYSTEM);
    if (chfState && CHF_STATE_CODE_VALUES.has(chfState)) {
      result.chfState = chfState as ChfStateCode;
    }

    const withMed = getCodeBySystem(entry, VITALL_CHF_WITH_MED_SYSTEM);
    if (withMed && WITH_MED_CODE_VALUES.has(withMed)) {
      result.withMed = withMed as WithMedCode;
    }
  }

  // Fallback: if no Vitall severity found, check old HL7 format on first entry
  if (!result.severity) {
    const hl7Code = getCodeBySystem(interpretation[0], HL7_OBS_INTERPRETATION_SYSTEM);
    if (hl7Code && SEVERITY_CODE_VALUES.has(hl7Code)) {
      result.severity = hl7Code as VitalInterpretationCode;
    }
  }

  return result;
};

interface ExtractedItem {
  display: string;
  text: string;
  interpretationCode?: string;
  valueString?: string;
}

/**
 * Utility function to extract items by type from an Observation resource.
 * @param observation - The FHIR Observation resource.
 * @param codeType - The code type to filter items (e.g., QUESTIONNAIRE_DOMAIN_REFERENCE_ITEM or QUESTIONNAIRE_RESPONSE_ITEM).
 * @returns An array of extracted items.
 */
export const extractItemsByType = (observation: fhirR4.Observation, codeType: string): ExtractedItem[] => {
  return (
    observation.component
      ?.filter(component => component?.code?.coding?.some(c => c.code === codeType))
      .map(component => {
        const extractedItem: ExtractedItem = {
          display: component.code?.coding?.[0]?.display || "",
          text: component.code?.text || "",
        };

        if (component.interpretation) {
          const v2Interpretation = component.interpretation.find(interp =>
            interp.coding?.some(c => c.system === OBSERVATION_INTERPRETATION_SYSTEM)
          );
          if (v2Interpretation) {
            extractedItem.interpretationCode = v2Interpretation.coding?.find(
              c => c.system === OBSERVATION_INTERPRETATION_SYSTEM
            )?.code;
          }
        }
        if (component.valueString) {
          extractedItem.valueString = component.valueString;
        }

        return extractedItem;
      }) || []
  );
};
